import React, { useCallback, useEffect, useState } from 'react';
import { IconLocation, IconSoup } from '@tabler/icons-react';
import { BoardDelayedClickUpTooltip } from './BoardDelayedClickUpTooltip';
import { formatLunchSinceTooltip } from '../utils/formatLunchTimeSaoPaulo';
import './TechnicianColumnTitle.css';

/**
 * Nome do técnico na coluna do board.
 * Clique simples: abre o Novo painel (mesmo do menu).
 * Duplo clique: minimizar coluna (filtro).
 * Ícones: avatar S3 (se houver), rastreável (Location) e/ou almoço aberto (Soup).
 */
export function TechnicianColumnTitle({
  technician,
  onSingleActivate,
  onDoubleActivate,
  canOpenNovoPainel = false,
  avatarUrl = null,
  trackable = false,
  lunchStartedAt = null,
  tooltipText,
}) {
  const resolvedTooltip =
    tooltipText ||
    (canOpenNovoPainel
      ? 'Clique para abrir o Novo painel · duplo clique para minimizar · arraste para reordenar a coluna.'
      : 'Duplo clique para minimizar · arraste para reordenar a coluna.');

  const stopBubble = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const handleClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.detail > 1) return;
    if (!canOpenNovoPainel) return;
    onSingleActivate?.(technician);
  }, [canOpenNovoPainel, onSingleActivate, technician]);

  const handleDoubleClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onDoubleActivate?.(technician);
  }, [onDoubleActivate, technician]);

  const lunchActive = lunchStartedAt != null && String(lunchStartedAt).trim() !== '';
  const lunchTooltip = formatLunchSinceTooltip(lunchStartedAt);
  const photoUrl = avatarUrl != null ? String(avatarUrl).trim() : '';
  const [avatarFailed, setAvatarFailed] = useState(false);

  useEffect(() => {
    setAvatarFailed(false);
  }, [photoUrl]);

  return (
    <span className="column-title-cluster">
      {photoUrl && !avatarFailed ? (
        <span
          className={`column-title-avatar${canOpenNovoPainel ? ' is-novo-painel' : ''}`}
          aria-hidden
          data-click-action={canOpenNovoPainel ? 'novo-painel' : undefined}
          onPointerDownCapture={stopBubble}
          onMouseDownCapture={stopBubble}
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
        >
          <img
            src={photoUrl}
            alt=""
            className="column-title-avatar-img"
            loading="lazy"
            decoding="async"
            draggable={false}
            onError={() => setAvatarFailed(true)}
          />
        </span>
      ) : null}
      <BoardDelayedClickUpTooltip
        className="technician-column-title-tooltip"
        text={resolvedTooltip}
        dismissOnInteract
      >
        <span
          className={`column-title${canOpenNovoPainel ? ' is-novo-painel' : ''}`}
          data-technician-column-title={technician}
          data-click-action={canOpenNovoPainel ? 'novo-painel' : undefined}
          onPointerDownCapture={stopBubble}
          onMouseDownCapture={stopBubble}
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
          onDragStart={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          draggable={false}
        >
          {technician}
        </span>
      </BoardDelayedClickUpTooltip>
      {trackable ? (
        <BoardDelayedClickUpTooltip
          className="technician-column-trackable-tip"
          text="Rastreável no App Go"
          delayMs={400}
          compact
          dismissOnInteract
        >
          <span className="column-title-trackable" aria-label="Rastreável no App Go">
            <IconLocation size={12} stroke={2.2} aria-hidden />
          </span>
        </BoardDelayedClickUpTooltip>
      ) : null}
      {lunchActive ? (
        <BoardDelayedClickUpTooltip
          className="technician-column-lunch-tip"
          text={lunchTooltip}
          delayMs={400}
          compact
          dismissOnInteract
        >
          <span className="column-title-lunch" aria-label={lunchTooltip}>
            <IconSoup size={12} stroke={2.2} aria-hidden />
          </span>
        </BoardDelayedClickUpTooltip>
      ) : null}
    </span>
  );
}
